import { useEffect, useMemo, useState } from "react";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import Highcharts from "highcharts";
import isToday from "date-fns/isToday";
import format from "date-fns/format";
import startOfDay from "date-fns/startOfDay";
import endOfDay from "date-fns/endOfDay";
import startOfHour from "date-fns/startOfHour";
import endOfHour from "date-fns/endOfHour";
import merge from "lodash/merge";
import { COMPLETE_COLOR, INCOMPLETE_COLOR } from "@constants/colors";
import {
    DISPLAY_HOUR_MINUTE,
    SERVER_DATE_TIME_FORMAT,
    MONTH_DAY_YEAR_INPUT_DATE_FORMAT
} from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import { DEFAULT_HIGHCHARTS_AREA_CHART_OPTIONS } from "@constants/highcharts";
import { REPORTS_TRANSACTIONS_URL } from "@constants/routes";
import { PolicyDecisionTypes } from "@enums/policy-decision-types";
import { TimeRangeTypes } from "@enums/time-range-types";
import { WorkflowReportIntervalTypes } from "@enums/workflow-report-interval-types";
import { WorkflowReportCompletionRatesOverTimeDataType, WorkflowReportCompletionRatesOverTimeResponse } from "@idw-types/data/reports";
import { WorkflowReportSearchFiltersState } from "@idw-types/state/workflow-report-state";
import { navigateToUtilWithQueryParameters } from "@utils/navigation-util";
import { HighchartsChartContainer } from "../../../../common/highcharts-chart-container/highcharts-chart-container";

type WorkflowReportCompletionRatesOverTimeAreaChartProps = {
    completionRatesOverTimeResponse: WorkflowReportCompletionRatesOverTimeResponse;
    searchFilters: WorkflowReportSearchFiltersState;
};

export const WorkflowReportCompletionRatesOverTimeAreaChart = (props: WorkflowReportCompletionRatesOverTimeAreaChartProps) => {
    const { completionRatesOverTimeResponse, searchFilters } = props;
    const navigate = useNavigate();
    const routerLocation = useLocation();

    const data = useMemo(() => {
        const series: Array<{ name: string, color: string, data: Array<number> }> = [];
        let numberDates: Array<number> = completionRatesOverTimeResponse.values.map(value => value.date);

        // if hourly and current day, filter out data past the current hour
        if (completionRatesOverTimeResponse.interval === WorkflowReportIntervalTypes.HOURLY && isToday(searchFilters.startDate)) {
            const currentHour = new Date().getHours();
            numberDates = numberDates.filter(numberDate => {
                const passedDate = new Date(numberDate);
                return passedDate.getHours() < currentHour;
            });
        }

        // get all formatted dates
        let categories: Array<Date> = [];
        if (completionRatesOverTimeResponse.interval === WorkflowReportIntervalTypes.DAILY) {
            const tzOffset = new Date().getTimezoneOffset() * 60 * 1000;
            categories = numberDates.map(numberDate => new Date(numberDate + tzOffset));
        } else {
            categories = numberDates.map(numberDate => new Date(numberDate));
        }

        const remainingValues: Array<WorkflowReportCompletionRatesOverTimeDataType> = [];
        completionRatesOverTimeResponse.values.forEach(value => {
            const numberDate = numberDates.find(nd => nd === value.date);
            if (numberDate) {
                remainingValues.push(value);
            }
        });

        series.push({
            name: resources.charts.workflowReportCompletionRatesOverTime.labels.complete,
            color: COMPLETE_COLOR,
            data: remainingValues.map(value => value.complete)
        });

        series.push({
            name: resources.charts.workflowReportCompletionRatesOverTime.labels.incomplete,
            color: INCOMPLETE_COLOR,
            data: remainingValues.map(value => value.incomplete)
        });

        return { series, categories };
    }, [completionRatesOverTimeResponse]);

    const [chartOptions, setChartOptions] = useState<Highcharts.Options>(merge({}, DEFAULT_HIGHCHARTS_AREA_CHART_OPTIONS, {
        chart: {
            height: "400"
        },
        title: {
            text: resources.charts.workflowReportCompletionRatesOverTime.title,
        },
        xAxis: {
            type: "datetime",
            categories: data.categories,
            labels: {
                formatter: function () {
                    return completionRatesOverTimeResponse.interval === WorkflowReportIntervalTypes.DAILY
                        ? format(this.value, MONTH_DAY_YEAR_INPUT_DATE_FORMAT)
                        : format(this.value, DISPLAY_HOUR_MINUTE);
                }
            }
        },
        yAxis: {
            labels: {
                format: '{value}%'
            }
        },
        tooltip: {
            formatter: function () {
                const points = this.points;
                const xAxisTooltipText = completionRatesOverTimeResponse.interval === WorkflowReportIntervalTypes.DAILY
                    ? format(this.x, MONTH_DAY_YEAR_INPUT_DATE_FORMAT)
                    : format(this.x, DISPLAY_HOUR_MINUTE);
                const tooltipArray = [xAxisTooltipText];

                points.forEach((point: any) => {
                    const percentage = point.percentage ? point.percentage.toFixed(1) : 0;
                    tooltipArray.push(`<span>${point.series.name}</span>: <b>${percentage}%</b> (${point.y})<br/>`);
                });

                return tooltipArray;
            }
        },
        plotOptions: {
            area: {
                cursor: "pointer",
                events: {
                    click: (event: any) => {
                        const category = event.point?.category;
                        const name = event.point?.series?.name;
                        let startDate: string;
                        let endDate: string;
                        if (completionRatesOverTimeResponse.interval === WorkflowReportIntervalTypes.DAILY) {
                            startDate = format(startOfDay(category), SERVER_DATE_TIME_FORMAT);
                            endDate = format(endOfDay(category), SERVER_DATE_TIME_FORMAT);
                        } else {
                            startDate = format(startOfHour(category), SERVER_DATE_TIME_FORMAT);
                            endDate = format(endOfHour(category), SERVER_DATE_TIME_FORMAT);
                        }

                        const queryParametersObject: any = {
                            startDate: startDate,
                            endDate: endDate,
                            timeRangeType: TimeRangeTypes.CUSTOM,
                            workflowIds: searchFilters.workflowId,
                            shouldSearchOnLoad: "true",
                            includeArchivedServicesAndWorkflows: `${searchFilters.includeArchivedWorkflows}`
                        };

                        if (searchFilters.appId) {
                            queryParametersObject.appId = searchFilters.appId;
                        }

                        if (searchFilters.country) {
                            queryParametersObject.country = searchFilters.country;
                        }

                        if (name === resources.charts.workflowReportCompletionRatesOverTime.labels.complete) {
                            queryParametersObject.policyDecisions = [PolicyDecisionTypes.APPROVED, PolicyDecisionTypes.DENIED].join(",");
                        } else {
                            queryParametersObject.policyDecisions = [PolicyDecisionTypes.OBLIGATION, PolicyDecisionTypes.NO_DECISION].join(",");
                        }

                        const queryParameters = createSearchParams(queryParametersObject).toString();
                        navigateToUtilWithQueryParameters(REPORTS_TRANSACTIONS_URL, navigate, routerLocation.pathname, queryParameters);
                    }
                }
            }
        },
        series: data.series,
        legend: ""
    }));

    useEffect(() => {
        const newChartOptions: Highcharts.Options = merge({}, chartOptions, {
            xAxis: {
                categories: data.categories
            },
            series: data.series
        });

        setChartOptions(newChartOptions);

    }, [data]);

    return <HighchartsChartContainer options={chartOptions} />;
};